<template>
  <div v-if="!fetchingOutputPresetFilters">
    <div class="default-table mt-0">
      <div class="table-actions">
        <b-row align-h="between">
          <b-col>
            <b-dropdown
              id="output-preset-filter-dropdown-1"
              :text="presetType.value"
              variant="bg-white"
              class="dropdown-icon-wrapper"
            >
              <template #button-content>
                <span>{{ presetType.value }}</span>
                <GjIcon
                  class="icon-right"
                  name="ArrowDown"
                />
              </template>
              <b-dropdown-item
                v-for="type in presetTypeOptions"
                :key="type.id"
                @click="presetType = type"
              >
                {{ type.value }}
              </b-dropdown-item>
            </b-dropdown>
          </b-col>
          <b-col class="d-flex justify-content-end">
            <b-input-group class="searchInputGroup ">
              <b-input-group-prepend is-text>
                <b-icon-search />
              </b-input-group-prepend>
              <b-form-input
                v-model="searchInput"
                type="search"
                class="searchInput"
                placeholder="Search by preset name"
              />
            </b-input-group>
          </b-col>
        </b-row>
      </div>

      <div class="position-relative">
        <b-table
          ref="table"
          :select-mode="selectMode"
          :items="presets"
          :fields="fields"
          :class="{ loaded: !fetchingOutputPresets }"
          show-empty
          selectable
          responsive
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #cell(name)="data">
            <router-link
              :to="{
                name: 'output-presets-view',
                params: { id: data.item.id },
              }"
              target="_blank"
            >
              {{ data.value }}
            </router-link>
          </template>

          <template #cell(description)="data">
            {{ data.value }}
          </template>

          <template #cell(category)="data">
            {{ data.value }}
          </template>

          <template #empty>
            <template v-if="fetchingOutputPresets">
              <div class="loading-table text-center py-3">
                <b-spinner variant="primary" />
              </div>
            </template>
            <template v-else>
              <no-data
                no-data-text="No presets have been added yet..."
                create-text="+ Create a custom preset"
                icon="Section"
                @action="createOutputPreset"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="fetchingOutputPresets && presets.length > 0" />
      </div>

      <div
        v-if="presets.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
  <loading-page v-else />
</template>
<script>
import { outputPresetTypes } from '@/helpers/encoder/output-presets'
import { BIconSearch } from 'bootstrap-vue'

export default {
  name: 'OutputPresetsModal',
  components: {
    BIconSearch,
  },
  props: {
    groupType: {
      type: Number,
      default: null,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
    tab: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selectMode: 'single',
      presetType: { value: 'All Presets', id: -1 },
      selectedCategory: { value: 'Any Category', id: 0 },
      searchInput: '',
      originsIndex: 1,
      cnamesIndex: 1,
      fields: [
        { key: 'selected', label: '' },
        { key: 'name', sortable: true },
        'description',
        'category',
        { key: 'presetType', label: 'Type' },
      ],
      selected: [],
      currentPage: 1,
    }
  },
  computed: {
    outputPresetTypes() {
      return outputPresetTypes
    },
    showCategoryFilter() {
      return this.presetType.id === 0
    },
    isSelected() {
      if (this.selected[0]) {
        return true
      }
      return false
    },
    presets() {
      return this.$store.state.outputPresets.outputPresets
    },
    presetTypeOptions() {
      return this.$store.state.outputPresets.outputPresetFilters.presetOptions
    },
    presetCategoryOptions() {
      return this.$store.state.outputPresets.outputPresetFilters
        .presetCategories
    },
    fetchingOutputPresetFilters() {
      return this.$store.state.outputPresets.fetchingOutputPresetFilters
    },
    resultInfo() {
      return this.$store.state.outputPresets.outputPresetsResultInfo
    },
    fetchingOutputPresets() {
      return this.$store.state.fetchingOutputPresets
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
  },
  watch: {
    currentPage() {
      this.fetchOutputPresets()
    },
    presetType() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },
    selectedCategory() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },
    searchInput() {
      if (this.currentPage === 1) {
        this.fetchOutputPresets()
      } else {
        this.currentPage = 1
      }
    },
    selected() {
      this.$emit('selected', this.selected ? this.selected[0] : null)
    },
  },
  created() {
    this.fetchOutputPresetFilters()
    this.fetchOutputPresets()
  },
  mounted() {
    this.isMounted = true
  },
  methods: {
    onRowSelected(items) {
      this.selected = items
    },
    deleteOutputPreset() {
      this.$store.dispatch(
        'outputPresets/deleteOutputPreset',
        this.selected[0].id,
      )
    },
    navigate(id) {
      this.$router.push(`/output-presets/${id}`)
    },
    fetchOutputPresets() {
      this.$store
        .dispatch('outputPresets/fetchOutputPresets', {
          type: this.presetType.id,
          pageIndex: this.currentPage - 1,
          outputGroupType: this.groupType,
          search: this.searchInput,
        })
        .catch(() => {
          this.errorToast('Something went wrong')
        })
    },
    fetchOutputPresetFilters() {
      this.$store
        .dispatch('outputPresets/fetchOutputPresetFilters')
        .catch(() => {
          this.errorToast('Something went wrong')
        })
    },
    createOutputPreset() {
      this.$store.commit('jobTemplates/SET_CREATING_OUTPUT_PRESET', true)
      this.$router.push({
        name: 'output-presets-new',
        params: { from: this.$route.name, activeTab: this.tab },
        query: this.$route.query,
      })
    },
  },
}
</script>

<style scoped>
.searchInputGroup {
  width: 250px;
}
.searchInput {
  margin-right: 0;
}
</style>
